import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import '../member/design/member.css';
import Icon, { IconName } from '../member/design/Icon';
import { GympeWordmark, inrExact } from '../member/design/atoms';
import { getPayRequest, startPayCheckout, verifyPayment, PayApiError, PayRequest, ClientCheckout } from './payApi';
import { backTarget } from './back';

/* The hosted pay page a WhatsApp payment link opens. Public — the token is the
   only credential. The gateway's webhook is the source of truth; this page
   verifies the payer's signed return only to say "Paid" without waiting, and
   otherwise polls until the payment lands. Works for any gateway: an SDK
   checkout where we know the provider, a redirect otherwise. */

type Phase =
  | { k: 'loading' }
  | { k: 'missing' }
  | { k: 'error'; message: string }
  | { k: 'ready'; req: PayRequest }
  | { k: 'paying'; req: PayRequest }
  | { k: 'confirming'; req: PayRequest }
  | { k: 'paid'; req: PayRequest };

const RETURN_KEYS = ['razorpay_payment_id', 'razorpay_order_id', 'razorpay_signature', 'fake_order_id', 'fake_payment_id', 'fake_signature'];
const POLL_MS = 3000;
const POLL_LIMIT = 40; // ~2 minutes, then ask them to check back

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const s = document.createElement('script');
    s.src = src;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Could not load the payment window. Check your connection and try again.'));
    document.body.appendChild(s);
  });
}

/** Open the gateway. Resolves with the signed result for SDK flows; redirects otherwise. */
async function openCheckout(checkout: ClientCheckout): Promise<Record<string, string> | null> {
  if (checkout.kind === 'sdk' && checkout.provider === 'razorpay') {
    await loadScript('https://checkout.razorpay.com/v1/checkout.js');
    const { callback_url: _redirectOnly, ...options } = checkout.payload;
    return new Promise((resolve, reject) => {
      const rzp = new (window as any).Razorpay({
        ...options,
        handler: (result: Record<string, string>) => resolve(result),
        modal: { ondismiss: () => resolve(null) },
      });
      rzp.on('payment.failed', (e: any) => reject(new Error(e?.error?.description || 'Payment failed. No money was taken — you can try again.')));
      rzp.open();
    });
  }
  const url = checkout.kind === 'redirect' ? checkout.url : checkout.fallbackUrl;
  if (!url) throw new Error('This payment method is not available on the web yet.');
  window.location.assign(url);
  return new Promise(() => {}); // navigating away
}

const clock = (ms: number) => {
  const sec = Math.ceil(ms / 1000);
  return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
};

/**
 * Time left on the weekly-times hold, recomputed from the server's expiry on
 * every tick (never decremented), against the server's clock.
 */
function useHoldLeft(req: PayRequest | null, onLapse: () => void): number | null {
  const [, tick] = useState(0);
  const offset = useRef(0);
  const expiresAt = req?.hold && ['created', 'sent'].includes(req.status) ? req.hold.expiresAt : null;
  useEffect(() => {
    if (req) offset.current = new Date(req.now).getTime() - Date.now();
  }, [req]);
  const left = expiresAt ? Math.max(0, new Date(expiresAt).getTime() - (Date.now() + offset.current)) : null;
  useEffect(() => {
    if (!expiresAt) return;
    const id = window.setInterval(() => tick(n => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [expiresAt]);
  const lapsed = left === 0;
  useEffect(() => {
    if (lapsed) onLapse();
  }, [lapsed, onLapse]);
  return left;
}

const fmtDay = (iso: string) => new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

export default function PayPage() {
  const { token = '' } = useParams();
  const [phase, setPhase] = useState<Phase>({ k: 'loading' });
  const [notice, setNotice] = useState<string | null>(null);
  const [back] = useState(() => backTarget(token));
  const goBack = back ? { label: back.label, onClick: () => window.location.assign(back.path) } : undefined;
  const polls = useRef(0);

  const load = useCallback(async () => {
    try {
      const req = await getPayRequest(token);
      setPhase(req.status === 'paid' ? { k: 'paid', req } : { k: 'ready', req });
      return req;
    } catch (e) {
      if (e instanceof PayApiError && e.status === 404) setPhase({ k: 'missing' });
      else setPhase({ k: 'error', message: e instanceof Error ? e.message : 'Could not load this payment' });
      return null;
    }
  }, [token]);

  // Poll until the payment is recorded (webhook) — after a verified return
  // that the gateway had not yet captured, or after a redirect back.
  const confirm = useCallback(async (req: PayRequest) => {
    setPhase({ k: 'confirming', req });
    polls.current = 0;
    const tick = async () => {
      polls.current += 1;
      const fresh = await getPayRequest(token).catch(() => null);
      if (fresh?.status === 'paid') return setPhase({ k: 'paid', req: fresh });
      if (polls.current >= POLL_LIMIT) {
        setNotice("We haven't received confirmation yet. If money left your account, it will show here shortly — you don't need to pay again.");
        return setPhase({ k: 'ready', req: fresh ?? req });
      }
      window.setTimeout(tick, POLL_MS);
    };
    window.setTimeout(tick, POLL_MS);
  }, [token]);

  const settle = useCallback(async (req: PayRequest, result: Record<string, string>) => {
    setPhase({ k: 'confirming', req });
    try {
      const { status } = await verifyPayment(token, result);
      if (status === 'paid') return setPhase({ k: 'paid', req: (await getPayRequest(token)) ?? req });
    } catch {
      /* fall through to polling — the webhook is authoritative */
    }
    confirm(req);
  }, [token, confirm]);

  useEffect(() => {
    (async () => {
      const req = await load();
      // Returning from a redirect-style gateway: report the result once, then clean the URL.
      const params = new URLSearchParams(window.location.search);
      const result: Record<string, string> = {};
      RETURN_KEYS.forEach(k => { const v = params.get(k); if (v) result[k] = v; });
      if (Object.keys(result).length) {
        window.history.replaceState(null, '', window.location.pathname);
        if (req && req.status !== 'paid') await settle(req, result);
      }
    })();
  }, [load, settle]);

  const pay = async (req: PayRequest) => {
    setNotice(null);
    setPhase({ k: 'paying', req });
    try {
      const { amount, checkout } = await startPayCheckout(token);
      const current = amount !== req.amount ? { ...req, amount } : req;
      const result = await openCheckout(checkout);
      if (!result) return setPhase({ k: 'ready', req: current }); // closed the window
      await settle(current, result);
    } catch (e) {
      if (e instanceof PayApiError && ['ALREADY_PAID', 'ALREADY_SETTLED', 'LINK_EXPIRED', 'LINK_CANCELLED'].includes(e.code ?? '')) {
        await load();
        return;
      }
      setNotice(e instanceof Error ? e.message : 'Something went wrong. No money was taken.');
      setPhase({ k: 'ready', req });
    }
  };

  const req = 'req' in phase ? phase.req : null;
  // The hold ran out on this page: re-read, and the server says it expired.
  const holdLeft = useHoldLeft(phase.k === 'ready' ? phase.req : null, useCallback(() => { load(); }, [load]));

  return (
    <div className="mapp" style={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', padding: '28px 16px 40px' }}>
      <div className="col" style={{ width: '100%', maxWidth: 420, gap: 16 }}>
        {req && (
          <div className="row" style={{ gap: 12 }}>
            {req.gym.logoUrl
              ? <img src={req.gym.logoUrl} alt="" className="avatar" style={{ objectFit: 'cover' }} />
              : <div className="avatar">{req.gym.name.slice(0, 1)}</div>}
            <div className="col" style={{ gap: 1, minWidth: 0 }}>
              <div className="dp dp-m trunc">{req.gym.name}</div>
              <div className="sub">Payment request</div>
            </div>
          </div>
        )}

        {phase.k === 'loading' && (
          <>
            <div className="skel" style={{ height: 56, borderRadius: 'var(--r-m)' }} />
            <div className="skel" style={{ height: 260, borderRadius: 'var(--r-m)' }} />
          </>
        )}

        {phase.k === 'missing' && (
          <Message icon="x" title="Link not found" body="This payment link doesn't exist. Check the link in your message, or ask your gym to send it again." />
        )}
        {phase.k === 'error' && <Message icon="help" title="Couldn't load this payment" body={phase.message} action={{ label: 'Try again', onClick: load }} />}

        {req && phase.k !== 'paid' && (req.status === 'expired' || req.status === 'cancelled') && (
          req.hold?.kind === 'seat' ? (
            req.status === 'expired' ? (
              <Message
                icon="clock"
                title="Your spot was released"
                body="Your spot was held while you paid, and the time ran out — so it went back to the gym. Nothing was charged. Book again to get a new one."
                action={goBack}
              />
            ) : (
              <Message
                icon="clock"
                title="This link was replaced"
                body="Pay for this booking from My bookings. Nothing was charged on this link."
                action={goBack}
              />
            )
          ) : req.hold ? (
            <Message
              icon="clock"
              title="Your times were released"
              body={req.hold.channel === 'web'
                ? 'Your weekly times were held for 10 minutes. Choose them again to finish — nothing was charged.'
                : 'Your weekly times were held for 10 minutes. Go back to the app to choose them again — nothing was charged.'}
              action={req.hold.channel === 'web' ? { label: 'Choose times again', onClick: () => window.location.assign('/app/plans') } : undefined}
            />
          ) : (
            <Message
              icon="clock"
              title={req.status === 'expired' ? 'This link has expired' : 'This link was replaced'}
              body={`Ask ${req.gym.name} to send you a new payment link. Nothing was charged.`}
            />
          )
        )}

        {req && ['ready', 'paying', 'confirming'].includes(phase.k) && ['created', 'sent'].includes(req.status) && (
          <>
            <div className="card" style={{ padding: 18 }}>
              <div className="col" style={{ gap: 4 }}>
                <span className="lbl">Amount due</span>
                <div className="num" style={{ fontSize: 34, fontWeight: 800 }}>{inrExact(req.amount)}</div>
                {req.payer.firstName && (
                  <div className="sub">For {req.payer.firstName}{req.payer.phone ? ` · ${req.payer.phone}` : ''}</div>
                )}
              </div>
            </div>

            {holdLeft != null && holdLeft > 0 && (
              <div className="card flat row" style={{ padding: '12px 14px', gap: 8 }} aria-live="polite">
                <Icon n="clock" s={16} />
                <span className="mid num">{req.hold?.kind === 'seat' ? 'Spot' : 'Times'} held for {clock(holdLeft)}</span>
                <span className="tiny" style={{ marginLeft: 'auto' }}>then released</span>
              </div>
            )}

            {req.sales.map((sale, i) => (
              <div key={sale.invoiceId ?? i} className="card" style={{ padding: '6px 16px 14px' }}>
                {sale.lines.map((l, j) => (
                  <div key={j} className="lrow">
                    <div className="col" style={{ gap: 1, flex: 1, minWidth: 0 }}>
                      <div className="mid trunc">{l.name}</div>
                      <div className="tiny">
                        {l.quantity > 1 ? `${l.quantity} × ${inrExact(l.price)}` : inrExact(l.price)}
                        {l.gstPercent > 0 ? ` · GST ${l.gstPercent}%` : ''}
                      </div>
                    </div>
                  </div>
                ))}
                <div className="col" style={{ gap: 6, paddingTop: 10 }}>
                  {sale.taxes.cgst > 0 && <Row label="CGST" value={inrExact(sale.taxes.cgst)} />}
                  {sale.taxes.sgst > 0 && <Row label="SGST" value={inrExact(sale.taxes.sgst)} />}
                  {sale.taxes.igst > 0 && <Row label="IGST" value={inrExact(sale.taxes.igst)} />}
                  <Row label="Total" value={inrExact(sale.total)} />
                  {sale.paid > 0 && <Row label="Already paid" value={`− ${inrExact(sale.paid)}`} />}
                  <Row label="Due" value={inrExact(sale.due)} strong />
                  {sale.invoiceId && (
                    <div className="tiny">{sale.documentType === 'tax_invoice' ? 'Tax invoice' : 'Bill of supply'} {sale.invoiceId}</div>
                  )}
                </div>
              </div>
            ))}

            {notice && (
              <div className="card flat" style={{ padding: 14 }}>
                <div className="sub wrap">{notice}</div>
              </div>
            )}

            <button className="btn gold block" disabled={phase.k !== 'ready'} onClick={() => pay(req)}>
              {phase.k === 'paying' ? 'Opening secure payment…'
                : phase.k === 'confirming' ? 'Confirming your payment…'
                : <>Pay {inrExact(req.amount)} <Icon n="right" s={17} /></>}
            </button>
            <div className="row" style={{ gap: 6, justifyContent: 'center' }}>
              <Icon n="shield" s={14} c="var(--ink-3)" />
              <span className="tiny">Secure payment{req.hold ? '' : ` · link valid until ${fmtDay(req.expiresAt)}`}</span>
            </div>
          </>
        )}

        {phase.k === 'paid' && (
          <div className="col" style={{ alignItems: 'center', gap: 6, paddingTop: 24, textAlign: 'center' }}>
            <div className="ok">
              <i /><i />
              <b><Icon n="check" s={38} /></b>
            </div>
            <div className="lbl-d">Payment received</div>
            <div className="dp dp-xl">{inrExact(phase.req.paidAmount ?? phase.req.amount)}</div>
            <div className="sub wrap">
              Paid to {phase.req.gym.name}{phase.req.paidAt ? ` on ${fmtDay(phase.req.paidAt)}` : ''}. Your receipt is on its way on WhatsApp.
            </div>
            {goBack && (
              <button className="btn ghost sm" style={{ marginTop: 12 }} onClick={goBack.onClick}>{goBack.label}</button>
            )}
          </div>
        )}

        <div className="row" style={{ justifyContent: 'center', marginTop: 8 }}>
          <span className="gympe-pill">Powered by <GympeWordmark /></span>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="between">
      <span className={strong ? 'mid' : 'sub'}>{label}</span>
      <span className={strong ? 'num mid' : 'num sub'}>{value}</span>
    </div>
  );
}

function Message({ icon, title, body, action }: { icon: IconName; title: string; body: string; action?: { label: string; onClick: () => void } }) {
  return (
    <div className="card" style={{ padding: 20 }}>
      <div className="col" style={{ gap: 10, alignItems: 'flex-start' }}>
        <div className="li"><Icon n={icon} s={18} /></div>
        <div className="dp dp-m">{title}</div>
        <div className="sub wrap">{body}</div>
        {action && <button className="btn ghost sm" onClick={action.onClick}>{action.label}</button>}
      </div>
    </div>
  );
}
